import { uuid } from '@10000/common'
import { useRequest, useView } from '@10000/react'
import { InputScreen, onboardBackground, useBackground, useShell, useStack } from '../../layout'
import { Body, Box, Card } from '../../widgets'
import { TeamLeader, TeamName } from './'
import { isPartner, subscriptions, valueColors, valueTextColors } from '../../../lookups'
import { PartnerOnboardConfirmation } from '../partner'
import { teamIsOnboarded } from '../'
import { cardRotations } from '../../../utilities.js'
import './Teams.scss'

export const Teams = ({ active, organisationId }) => {
  const { user: { admin, id }, teams, subscription } = useView('tenant')
  const shownTeams = organisationId ? teams.filter(team => team.organisation === organisationId) : teams

  const { push } = useStack()
  const request = useRequest('tenant')
  const teamLimit = subscriptions[subscription?.index]?.teamLimit || 0
  const canSkip = admin || (teams.every(x => teamIsOnboarded(x, id)) || teams.length === 0)

  useShell({ active, mode: 'navigate narrow' })
  useBackground().set(mobile => ({ active, fade: !admin, ...onboardBackground(admin, mobile) }))

  const modifyTeam = id => push(admin ? TeamLeader : TeamName, { props: { id, organisationId } })
  const createTeam = () => {
    const id = uuid()
    return request('teamAdded', { id, organisation: organisationId }).then(() => push(TeamLeader, { props: { id, organisationId } }))
  }

  const complete = () => isPartner(subscription) ? push(PartnerOnboardConfirmation, { props: { organisationId } }) : request('teamSetupComplete')

  const teamOnboarded = teamIndex => teamIsOnboarded(admin ? shownTeams[teamIndex] : teams[teamIndex], admin)

  return <InputScreen
    title="Set up your teams"
    description={!isPartner(subscription) &&
      <>
        You must set up <Body alt bold italic>at least one</Body> of your teams before you can start your first Squiz
      </>
    }
    altFooterButton={canSkip ? 'Finish' : undefined}
    altOnClick={canSkip ? complete : undefined}
  >
    {(admin && teamLimit) &&
      <Body align="center">
        {`${teamLimit - teams.length}/${teamLimit} available`}
        {teamLimit === teams.length &&
          <><br/>You can&apos;t add any more teams, upgrade your subscription in settings</>
        }
      </Body>
    }
    <Box fullWidth column gap={8}>
      {(admin ? shownTeams : teams).map(({ id, name }, index) =>
        <Card
          onClick={() => modifyTeam(id)}
          key={id}
          height={101}
          cursor="pointer"
          transform={`rotate(${teamOnboarded(index) ? 0 : cardRotations[index % cardRotations.length]}deg)`}
          shadow={false}
          background={teamOnboarded(index) ? valueColors[(index + 1) % valueColors.length] : 'black'}
          className="team-card"
        >
          <Body bold alt color={teamOnboarded(index) ? valueTextColors[(index + 1) % valueTextColors.length] : 'white'}>{name || 'Unnamed team'}</Body>
          {teamOnboarded(index) &&
            <Box
              className="shapes-gif"
              height={212}
              width={212}
              style={{
                position: "absolute",
                right: -68,
                top: -53
              }}
            />
          }
        </Card>
      )}
      {((teamLimit > teams.length) && admin) &&
        <Card
          onClick={createTeam}
          height={101}
          cursor="pointer"
          shadow={false}
        >
          <Body bold alt>+ Add team</Body>
        </Card>
      }
    </Box>
  </InputScreen>
}